import NextImage from 'next/image'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import {
  useAPIGetFollowUpDocument,
  useAPIGetFollowUpReflectionDocument,
  useAPIPelaksanaanProgress,
  useAPIUpsertFollowUpReflectionDocument,
} from 'api/kinerja/useAPIKinerja'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
} from 'app/KinerjaPage/pages/PelaksanaanV2/components/Card'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { SaveToDraftDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SaveToDraftDialog'
import { SubmitConfirmationDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SubmitConfirmationDialog'
import { TujuanDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/TujuanDialog'
import { FOLLOW_UP_REFLECTION_CONTENT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/reflection'
import { DUKUNGAN_UPAYA_TINDAK_LANJUT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/upayaTindakLanjut'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS } from 'app/KinerjaShared/constants/pelaksanaanSteps'
import useBeforePopState from '../hooks/useBeforePopState'

import type { IFollowUpReflectionDocumentForm } from 'types/kinerja-pelaksanaan'

type ActiveDialogTypes =
  | 'TUJUAN'
  | 'BELAJAR_PMM'
  | 'BELAJAR_LAINNYA'
  | 'SAVE_TO_DRAFT'
  | 'SUBMIT'
  | 'NONE'

interface ValidatedSupportNeedTypes {
  additionalSupportNeed: string
  supportNeed: string
}

export function DokumenRefleksiTindakLanjut() {
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const [activeDialog, setActiveDialog] = useState<ActiveDialogTypes>('NONE')

  const CURRENT_SCREEN_NAME = 'form_d'
  const ENTRY_POINT = 'pelaksanaan_hub'

  useEffect(() => {
    trackEvent({
      activity_type: 'pelaksanaan_open_form_d',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formMethods = useForm<IFollowUpReflectionDocumentForm>({
    mode: 'onChange',
    defaultValues: {
      achievement: '',
      challenge: '',
      improvementPlan: '',
      inspiration: '',
      additionalAchievement: '',
      additionalChallenge: '',
      additionalImprovementPlan: '',
      additionalInspiration: '',
    },
  })

  const { data: dataPelaksanaanProgress } = useAPIPelaksanaanProgress({
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isEvaluated =
    dataPelaksanaanProgress?.data?.evaluationDocumentState === 'READY'

  const { data: followUpDocumentData, isLoading: isLoadingFollowUpDocument } =
    useAPIGetFollowUpDocument({
      options: {
        useErrorBoundary: true,
        retry: false,
      },
    })

  const {
    isLoading: isLoadingFollowUpReflectionDocument,
    data: followUpReflectionDocumentData,
  } = useAPIGetFollowUpReflectionDocument({
    options: {
      useErrorBoundary: false,
      retry: false,
      onSuccess: (res) => {
        // Set data from BE as initial form data
        formMethods.reset(res.data?.detail)
      },
    },
  })

  const upsertMutation = useAPIUpsertFollowUpReflectionDocument({
    useErrorBoundary: false,
  })

  const isSubmitted =
    followUpReflectionDocumentData?.data?.state === 'SUBMITTED'

  const isSaveDraftDisabled =
    !formMethods.formState.isDirty ||
    upsertMutation.isLoading ||
    isLoadingFollowUpDocument ||
    isLoadingFollowUpReflectionDocument ||
    isEvaluated
  const isSubmitDisabled =
    !formMethods.formState.isValid ||
    upsertMutation.isLoading ||
    isLoadingFollowUpDocument ||
    isLoadingFollowUpReflectionDocument ||
    isEvaluated

  const handleSubmit = formMethods.handleSubmit(
    (detail: IFollowUpReflectionDocumentForm) => {
      if (isSubmitDisabled) return

      upsertMutation.mutate(
        { state: 'SUBMITTED', detail },
        {
          onSuccess: () => {
            setActiveDialog('NONE')
            router.replace(
              `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/beranda`
            )

            toast({ message: 'Berhasil kumpulkan dokumen!', type: 'success' })
          },
          onError: () => {
            setActiveDialog('NONE')
            toast({ message: 'Gagal kumpulkan dokumen. Coba lagi' })
          },
        }
      )
    }
  )

  const handleSaveToDraft = () => {
    if (isSaveDraftDisabled) return

    upsertMutation.mutate(
      { state: 'DRAFT', detail: formMethods.getValues() },
      {
        onSuccess: () => {
          setActiveDialog('NONE')
          router.back()

          toast({ message: 'Berhasil simpan draf!', type: 'success' })
        },
        onError: () => {
          setActiveDialog('NONE')
          toast({ message: 'Gagal simpan draf. Coba lagi' })
        },
      }
    )
  }

  const hasBelajarLainnya = useMemo(() => {
    if (followUpDocumentData) {
      const {
        additionalFollowUpTarget,
        additionalFollowUpEffort,
        additionalFollowUpPeriod,
        additionalSupportNeed,
      } = followUpDocumentData.data.detail

      return (
        !!additionalFollowUpTarget ||
        !!additionalFollowUpEffort ||
        !!additionalFollowUpPeriod ||
        !!additionalSupportNeed
      )
    }

    return false
  }, [followUpDocumentData])

  const hasBelajarPMM = useMemo(() => {
    if (followUpDocumentData) {
      const { followUpTargets, followUpPeriod, supportNeed } =
        followUpDocumentData.data.detail

      return !!followUpTargets.length || !!followUpPeriod || !!supportNeed
    }

    return false
  }, [followUpDocumentData])

  const validatedSupportNeeds: ValidatedSupportNeedTypes = useMemo(() => {
    if (!followUpDocumentData) {
      return {
        supportNeed: '-',
        additionalSupportNeed: '-',
      }
    }

    const supportNeed = `${followUpDocumentData?.data?.detail?.supportNeed}`
    const additionalSupportNeed = `${followUpDocumentData?.data?.detail?.additionalSupportNeed}`

    const validatedSupportNeed = DUKUNGAN_UPAYA_TINDAK_LANJUT.includes(
      supportNeed
    )
      ? supportNeed
      : `Lainnya: ${supportNeed}`

    const validatedAdditionalSupportNeed =
      DUKUNGAN_UPAYA_TINDAK_LANJUT.includes(additionalSupportNeed)
        ? additionalSupportNeed
        : `Lainnya: ${additionalSupportNeed}`

    return {
      supportNeed: validatedSupportNeed,
      additionalSupportNeed: validatedAdditionalSupportNeed,
    }
  }, [followUpDocumentData])

  const handleBackSave = useCallback(() => {
    if (isSaveDraftDisabled) return true
    upsertMutation.mutate({ state: 'DRAFT', detail: formMethods.getValues() })
    return true
  }, [formMethods, isSaveDraftDisabled, upsertMutation])

  useBeforePopState(router, handleBackSave)

  const handleClickTargetButton = () => {
    trackEvent({
      activity_type: 'pelaksanaan_click_form_d_tujuan_formulir',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
    setActiveDialog('TUJUAN')
  }
  const handleClickUnderstandTargetButton = () => {
    trackEvent({
      activity_type: 'pelaksanaan_click_form_d_mengerti_tujuan_formulir',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
    setActiveDialog('NONE')
  }

  const handleClickSaveDraftButton = () => {
    trackEvent({
      activity_type: 'pelaksanaan_click_form_d_simpan_draf',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
    setActiveDialog('SAVE_TO_DRAFT')
  }

  const handleClickSubmitButton = () => {
    trackEvent({
      activity_type: 'pelaksanaan_click_form_d_kumpulkan',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
    setActiveDialog('SUBMIT')
  }

  const handleClickConfirmSubmitDialog = () => {
    trackEvent({
      activity_type: 'pelaksanaan_click_form_d_konfirmasi_kumpulkan',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: ENTRY_POINT,
    })
    handleSubmit()
  }

  return (
    <div className="px-8 pt-14 pb-8 bg-gray-10 min-h-full">
      <div className="max-w-6xl mx-auto">
        <Header
          title={
            PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS[
              'DOCUMENT_REFLECTION_FOLLOW_UP'
            ].title
          }
          className="mb-9"
          onBack={() => {
            if (isSaveDraftDisabled) {
              router.back()
            } else {
              setActiveDialog('SAVE_TO_DRAFT')
            }
          }}
        />
      </div>
      {isLoadingFollowUpDocument || isLoadingFollowUpReflectionDocument ? (
        <div className="w-full flex justify-center">
          <Spinner />
        </div>
      ) : (
        <form className="max-w-5xl mx-auto">
          <section className="p-6 space-y-3 bg-white rounded-lg mb-9">
            <Text className="!text-black" variant="heading-sm" as="h2">
              Langkah-langkah Refleksi Tindak Lanjut
            </Text>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Text className="!text-blue-60" variant="action">
                  Lakukan refleksi
                </Text>
                <Text>
                  Capaian dan tantangan yang dialami setelah tindak lanjut.
                </Text>
              </div>

              <div className="flex space-x-3">
                <Icon color="informational">arrow_circle_right</Icon>
                <div>
                  <Text className="!text-blue-60" variant="action">
                    Diskusi dengan atasan
                  </Text>
                  <Text>Agar lebih mudah isi formulir.</Text>
                </div>
              </div>

              <div className="flex space-x-3">
                <Icon color="informational">arrow_circle_right</Icon>
                <div>
                  <Text className="!text-blue-60" variant="action">
                    Lakukan Upaya Perbaikan
                  </Text>
                  <Text>Untuk membantu Anda meningkatkan kinerja.</Text>
                </div>
              </div>
            </div>
          </section>

          <Card>
            <CardHeader className="flex space-x-4">
              <Text variant="heading-lg" className="flex-1" as="h2">
                Formulir Refleksi Tindak Lanjut
              </Text>

              <Button
                color="blue"
                variant="ghost"
                size="sm"
                className="space-x-1"
                aria-haspopup={true}
                aria-expanded={activeDialog === 'TUJUAN'}
                onClick={handleClickTargetButton}
              >
                <Icon fontSize="sm" color="informational">
                  help
                </Icon>
                <span>Apa tujuan formulir ini?</span>
              </Button>
            </CardHeader>

            <CardBody className="py-6 px-9 space-y-4">
              {/* Pilihan Belajar */}
              {hasBelajarLainnya && (
                <div className="space-y-4">
                  <div className="flex space-x-4 items-center">
                    <NextImage
                      src="/illustrations/img-assessment-subject-book.png"
                      width="64"
                      height="64"
                    />
                    <Text variant="heading-md" className="flex-1" as="h3">
                      Pilihan Belajar
                    </Text>
                  </div>
                  <div className="flex space-x-9 items-start">
                    <div className="bg-surface-subdued rounded p-3 max-w-full w-80 flex-shrink-0 flex-grow-0">
                      <dl>
                        <Text as="dt" variant="body-sm-bold">
                          Tujuan Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpTarget || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Upaya Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpEffort || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Periode Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail
                            ?.additionalFollowUpPeriod || '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Kebutuhan Dukungan (Apa/Siapa)
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {validatedSupportNeeds.additionalSupportNeed}
                        </Text>
                      </dl>
                      <Button
                        size="sm"
                        color="white"
                        aria-haspopup={true}
                        aria-expanded={activeDialog === 'BELAJAR_LAINNYA'}
                        aria-label="Baca selengkapnya Pilihan Belajar"
                        onClick={() => setActiveDialog('BELAJAR_LAINNYA')}
                      >
                        Selengkapnya <Icon>chevron_right</Icon>
                      </Button>
                    </div>
                    <div className="space-y-4 w-full">
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT.additionalInspiration
                              ?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalInspiration"
                            color="subdued"
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT.additionalInspiration
                                ?.label
                            }
                          </Text>
                          <Textarea
                            {...formMethods.register('additionalInspiration', {
                              required: true,
                              maxLength: 500,
                            })}
                            isDisabled={isSubmitted || isEvaluated}
                            id="additionalInspiration"
                            placeholder="Ketik di sini"
                            maxLength={500}
                          />
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT.additionalAchievement
                              ?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalAchievement"
                            color="subdued"
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT.additionalAchievement
                                ?.label
                            }
                          </Text>
                          <Textarea
                            {...formMethods.register('additionalAchievement', {
                              required: true,
                              maxLength: 500,
                            })}
                            isDisabled={isSubmitted || isEvaluated}
                            id="additionalAchievement"
                            className="w-full"
                            placeholder="Contoh: Saya mendapat umpan balik yang menyadarkan bahwa murid khawatir masuk kelompok yang tidak efektif."
                            maxLength={500}
                          />
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT.additionalChallenge
                              ?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalChallenge"
                            color="subdued"
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT.additionalChallenge
                                ?.label
                            }
                          </Text>
                          <Textarea
                            {...formMethods.register('additionalChallenge', {
                              required: true,
                              maxLength: 500,
                            })}
                            isDisabled={isSubmitted || isEvaluated}
                            id="additionalChallenge"
                            placeholder="Contoh: Saya sulit mengajak guru lain praktik simulasi setelah jam mengajar."
                            maxLength={500}
                          />
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT
                              .additionalImprovementPlan?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="additionalImprovementPlan"
                            color="subdued"
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT
                                .additionalImprovementPlan?.label
                            }
                          </Text>
                          <Textarea
                            {...formMethods.register(
                              'additionalImprovementPlan',
                              {
                                required: true,
                                maxLength: 500,
                              }
                            )}
                            isDisabled={isSubmitted || isEvaluated}
                            id="additionalImprovementPlan"
                            placeholder="Contoh: Minta bantuan atasan untuk mengumumkan di rapat."
                            maxLength={500}
                          />
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </div>
              )}

              {/* Pilihan Belajar PMM */}
              {hasBelajarPMM && (
                <div className="space-y-4">
                  <div className="flex space-x-4 items-center">
                    <NextImage
                      src="/illustrations/img-smartphone-star.png"
                      width="64"
                      height="64"
                    />
                    <Text variant="heading-md" className="flex-1" as="h3">
                      Pilihan Belajar dari Platform Merdeka Mengajar (PMM)
                    </Text>
                  </div>
                  <div className="flex space-x-9 items-start">
                    <div className="bg-surface-subdued rounded p-3 max-w-full w-80 flex-shrink-0 flex-grow-0">
                      <dl>
                        <Text as="dt" variant="body-sm-bold">
                          Tujuan Tindak Lanjut
                        </Text>
                        <dd className="mb-6">
                          <ul className="list-disc pl-4">
                            {followUpDocumentData?.data?.detail?.followUpTargets?.map(
                              (followUpTarget) => (
                                <li key={followUpTarget.id}>
                                  <Text
                                    variant="body-sm"
                                    className="line-clamp-3 overflow-wrap-anywhere"
                                  >
                                    {followUpTarget.subIndicator}
                                  </Text>
                                </li>
                              )
                            )}
                          </ul>
                        </dd>

                        <Text as="dt" variant="body-sm-bold">
                          Periode Tindak Lanjut
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {followUpDocumentData?.data?.detail?.followUpPeriod ||
                            '-'}
                        </Text>

                        <Text as="dt" variant="body-sm-bold">
                          Kebutuhan Dukungan (Apa/Siapa)
                        </Text>
                        <Text
                          as="dd"
                          variant="body-sm"
                          className="mb-6 line-clamp-3 overflow-wrap-anywhere"
                        >
                          {validatedSupportNeeds.supportNeed}
                        </Text>
                      </dl>
                      <Button
                        size="sm"
                        color="white"
                        aria-haspopup={true}
                        aria-expanded={activeDialog === 'BELAJAR_PMM'}
                        aria-label="Baca selengkapnya Pilihan Belajar dari PMM"
                        onClick={() => setActiveDialog('BELAJAR_PMM')}
                      >
                        Selengkapnya <Icon>chevron_right</Icon>
                      </Button>
                    </div>
                    <div className="space-y-4 w-full">
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {FOLLOW_UP_REFLECTION_CONTENT.inspiration?.question}
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="inspiration"
                            color="subdued"
                          >
                            {FOLLOW_UP_REFLECTION_CONTENT.inspiration?.label}
                          </Text>
                          <Textarea
                            {...formMethods.register('inspiration', {
                              required: true,
                              maxLength: 500,
                            })}
                            isDisabled={isSubmitted}
                            id="inspiration"
                            placeholder="Ketik di sini"
                            maxLength={500}
                          />
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {FOLLOW_UP_REFLECTION_CONTENT.achievement?.question}
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="achievement"
                            color="subdued"
                          >
                            {FOLLOW_UP_REFLECTION_CONTENT.achievement?.label}
                          </Text>
                          <Textarea
                            {...formMethods.register('achievement', {
                              required: true,
                              maxLength: 500,
                            })}
                            isDisabled={isSubmitted}
                            id="achievement"
                            placeholder="Contoh: Saya mendapat umpan balik yang menyadarkan bahwa murid khawatir masuk kelompok yang tidak efektif."
                            maxLength={500}
                          />
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {FOLLOW_UP_REFLECTION_CONTENT.challenge?.question}
                        </Text>
                        <div className="space-y-1">
                          <Text as="label" htmlFor="challenge" color="subdued">
                            {FOLLOW_UP_REFLECTION_CONTENT.challenge?.label}
                          </Text>
                          <Textarea
                            {...formMethods.register('challenge', {
                              required: true,
                              maxLength: 500,
                            })}
                            isDisabled={isSubmitted}
                            id="challenge"
                            placeholder="Contoh: Saya sulit mengajak guru lain praktik simulasi setelah jam mengajar."
                            maxLength={500}
                          />
                        </div>
                      </fieldset>
                      <fieldset className="space-y-4">
                        <Text variant="heading-sm">
                          {
                            FOLLOW_UP_REFLECTION_CONTENT.improvementPlan
                              ?.question
                          }
                        </Text>
                        <div className="space-y-1">
                          <Text
                            as="label"
                            htmlFor="improvementPlan"
                            color="subdued"
                          >
                            {
                              FOLLOW_UP_REFLECTION_CONTENT.improvementPlan
                                ?.label
                            }
                          </Text>
                          <Textarea
                            {...formMethods.register('improvementPlan', {
                              required: true,
                              maxLength: 500,
                            })}
                            isDisabled={isSubmitted}
                            id="improvementPlan"
                            placeholder="Contoh: Minta bantuan atasan untuk mengumumkan di rapat."
                            maxLength={500}
                          />
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </div>
              )}
            </CardBody>

            {!isSubmitted && (
              <CardFooter className="justify-end space-x-3 px-6 py-6">
                <Button
                  size="lg"
                  color="white"
                  type="button"
                  disabled={isSaveDraftDisabled}
                  onClick={handleClickSaveDraftButton}
                >
                  Simpan draf
                </Button>
                <Button
                  size="lg"
                  color="blue"
                  type="button"
                  disabled={isSubmitDisabled}
                  onClick={handleClickSubmitButton}
                >
                  Kumpulkan
                </Button>
              </CardFooter>
            )}
          </Card>

          <TujuanDialog
            title="Formulir Refleksi Tindak Lanjut"
            suggestion="Diskusi dengan atasan agar tujuan formulir ini tercapai."
            isOpen={activeDialog === 'TUJUAN'}
            onClose={handleClickUnderstandTargetButton}
            content={[
              {
                title: 'Refleksi upaya tindak lanjut',
                description:
                  'Guru menyampaikan refleksi terhadap upaya tindak lanjut yang sudah dilakukan kepada atasan. ',
              },
              {
                title: 'Menyepakati upaya peningkatan',
                description:
                  'Guru dan atasan menyepakati upaya peningkatan untuk mengatasi tantangan dalam upaya tindak lanjut.',
              },
            ]}
          />

          <DetailDialog
            followUpPeriod={followUpDocumentData?.data?.detail?.followUpPeriod}
            followUpTarget={followUpDocumentData?.data?.detail?.followUpTargets?.map(
              ({ subIndicator }) => subIndicator
            )}
            isOpen={activeDialog === 'BELAJAR_PMM'}
            supportNeed={validatedSupportNeeds.supportNeed}
            title="Pilihan Belajar dari Platform Merdeka Mengajar (PMM)"
            onClose={() => setActiveDialog('NONE')}
          />

          <DetailDialog
            followUpEffort={
              followUpDocumentData?.data?.detail?.additionalFollowUpEffort
            }
            followUpPeriod={
              followUpDocumentData?.data?.detail?.additionalFollowUpPeriod
            }
            followUpTarget={
              followUpDocumentData?.data?.detail?.additionalFollowUpTarget
            }
            isOpen={activeDialog === 'BELAJAR_LAINNYA'}
            supportNeed={validatedSupportNeeds.additionalSupportNeed}
            title="Pilihan Belajar"
            onClose={() => setActiveDialog('NONE')}
          />

          <SubmitConfirmationDialog
            isOpen={activeDialog === 'SUBMIT'}
            onCancel={() => setActiveDialog('NONE')}
            isLoading={upsertMutation.isLoading}
            onConfirm={handleClickConfirmSubmitDialog}
          />

          <SaveToDraftDialog
            isOpen={activeDialog === 'SAVE_TO_DRAFT'}
            isLoading={upsertMutation.isLoading}
            onCancel={() => setActiveDialog('NONE')}
            onConfirm={handleSaveToDraft}
          />
        </form>
      )}
    </div>
  )
}

type DetailDialogProps = Omit<DialogProps, 'children'> & {
  title: string
  followUpEffort?: string
  followUpPeriod: string
  followUpTarget: Array<string> | string
  supportNeed: string
}

export function DetailDialog({
  followUpEffort,
  followUpPeriod,
  followUpTarget,
  supportNeed,
  title,
  ...dialogProps
}: DetailDialogProps) {
  const renderFollowUpTarget = () => {
    if (Array.isArray(followUpTarget)) {
      return (
        <dd>
          <ul className="list-disc pl-4">
            {followUpTarget.map((item, i) => (
              <li key={i}>
                <Text
                  variant="body-sm"
                  color="subdued"
                  className="line-clamp-3 overflow-wrap-anywhere"
                >
                  {item}
                </Text>
              </li>
            ))}
          </ul>
        </dd>
      )
    }

    return (
      <Text
        as="dd"
        className="overflow-wrap-anywhere"
        color="subdued"
        variant="body-sm"
      >
        {followUpTarget || '-'}
      </Text>
    )
  }

  return (
    <Dialog size="md" {...dialogProps}>
      <DialogOverlay />
      <DialogContent>
        <DialogHeader title={title} />

        <div className="p-6 flex flex-col space-y-4">
          <dl className="border border-disabled rounded">
            <div className="p-3 border-b border-disabled last:border-0">
              <Text as="dt">Tujuan Tindak Lanjut</Text>
              {renderFollowUpTarget()}
            </div>
            {followUpEffort && (
              <div className="p-3 border-b border-disabled last:border-0">
                <Text as="dt">Upaya Tindak Lanjut</Text>
                <Text
                  as="dd"
                  className="overflow-wrap-anywhere"
                  color="subdued"
                  variant="body-sm"
                >
                  {followUpEffort || '-'}
                </Text>
              </div>
            )}
            <div className="p-3 border-b border-disabled last:border-0">
              <Text as="dt">Periode Tindak Lanjut</Text>
              <Text
                as="dd"
                className="overflow-wrap-anywhere"
                color="subdued"
                variant="body-sm"
              >
                {followUpPeriod || '-'}
              </Text>
            </div>
            <div className="p-3 border-b border-disabled last:border-0">
              <Text as="dt">Kebutuhan Dukungan (Apa/Siapa)</Text>
              <Text
                as="dd"
                className="overflow-wrap-anywhere"
                color="subdued"
                variant="body-sm"
              >
                {supportNeed || '-'}
              </Text>
            </div>
          </dl>

          <div className="flex justify-center items-center">
            <Button onClick={() => dialogProps?.onClose?.()}>Kembali</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
